import React, { useState, useEffect, useCallback } from 'react';
import Layout from './layout/Layout';
import { useAuth } from '../context/AuthContext';
import { buildAuthHeaders } from '../services/authStorage';
import './MobileDownload.css';

// ============================================================================
// Expo Server Status Component (Watchdog)
// ============================================================================
const ExpoServerStatus = () => {
  const [status, setStatus] = useState({ status: 'checking', url: '' });
  const [lastUpdated, setLastUpdated] = useState(null);

  const checkStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/mobile/expo-status');
      if (response.ok) {
        const data = await response.json();
        setStatus(data.data);
        setLastUpdated(new Date());
      } else {
        setStatus({ status: 'error', url: '' });
      }
    } catch (err) {
      setStatus({ status: 'error', url: '' });
    }
  }, []);

  useEffect(() => {
    checkStatus();
    // Poll every 30 seconds
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  const getStatusConfig = () => {
    switch (status.status) {
      case 'online':
        return { icon: '🟢', text: 'Online', className: 'online' };
      case 'degraded':
        return { icon: '🟡', text: 'Degraded', className: 'degraded' };
      case 'offline':
        return { icon: '🔴', text: 'Offline', className: 'offline' };
      case 'checking':
        return { icon: '⏳', text: 'Checking...', className: 'checking' };
      default:
        return { icon: '❓', text: 'Unknown', className: 'unknown' };
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <div className={`expo-status-card ${statusConfig.className}`}>
      <div className="expo-status-header">
        <span className="expo-status-icon">{statusConfig.icon}</span>
        <span className="expo-status-title">Expo Development Server</span>
      </div>
      <div className="expo-status-details">
        <div className="expo-status-row">
          <span className="expo-status-label">Status:</span>
          <span className={`expo-status-value ${statusConfig.className}`}>{statusConfig.text}</span>
        </div>
        {status.responseTime && (
          <div className="expo-status-row">
            <span className="expo-status-label">Response Time:</span>
            <span className="expo-status-value">{status.responseTime}ms</span>
          </div>
        )}
        {lastUpdated && (
          <div className="expo-status-row">
            <span className="expo-status-label">Last Checked:</span>
            <span className="expo-status-value">{lastUpdated.toLocaleTimeString()}</span>
          </div>
        )}
      </div>
      <button className="expo-status-refresh" onClick={checkStatus} title="Refresh status">
        🔄
      </button>
    </div>
  );
};

// ============================================================================
// QR Code Display Component (M004-D004-F003, F004, F005)
// ============================================================================
const QRCodeDisplay = ({ type, title, description, fallbackUrl, fallbackLabel }) => {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchQRCode = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/mobile/qr/${type}`, {
        headers: buildAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to generate QR code');
      }
      
      const data = await response.json();
      setQrData(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    fetchQRCode();
  }, [fetchQRCode]);

  const handleCopyLink = async () => {
    const urlToCopy = qrData?.url || fallbackUrl;
    if (urlToCopy) {
      try {
        await navigator.clipboard.writeText(urlToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleOpenLink = () => {
    const urlToOpen = qrData?.url || fallbackUrl;
    if (urlToOpen) {
      window.open(urlToOpen, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="qr-code-container">
        <div className="qr-placeholder loading">
          <div className="spinner"></div>
          <span>Generating QR code...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="qr-code-container">
        <div className="qr-placeholder error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button className="retry-btn" onClick={fetchQRCode}>Retry</button>
        </div>
        {fallbackUrl && (
          <div className="qr-fallback">
            <a href={fallbackUrl} target="_blank" rel="noopener noreferrer" className="fallback-link">
              {fallbackLabel || 'Open Link'}
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="qr-code-container">
      <h3 className="qr-title">{title}</h3>
      <p className="qr-description">{description}</p>
      <div className="qr-image-wrapper">
        <img src={qrData?.qrCode} alt={`QR Code for ${title}`} className="qr-image" />
      </div>
      {/* Display URL below QR code */}
      {qrData?.url && (
        <div className="qr-url-display">
          <code>{qrData.url}</code>
        </div>
      )}
      <div className="qr-actions">
        <button className="qr-action-btn copy" onClick={handleCopyLink}>
          {copied ? '✓ Copied!' : '📋 Copy Link'}
        </button>
        <button className="qr-action-btn open" onClick={handleOpenLink}>
          🔗 Open Link
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Platform Tabs Component (M004-D004-F002)
// ============================================================================
const PlatformTabs = ({ activeTab, onTabChange }) => {
  return (
    <div className="platform-selector">
      <h2 className="platform-selector-title">Select Your App</h2>
      <div className="platform-tabs">
        <button 
          className={`tab-btn ${activeTab === 'ios' ? 'active' : ''}`}
          onClick={() => onTabChange('ios')}
        >
          <span className="tab-icon">🍎</span>
          <span className="tab-label">iOS (iPhone/iPad)</span>
        </button>
        <button 
          className={`tab-btn ${activeTab === 'android' ? 'active' : ''}`}
          onClick={() => onTabChange('android')}
        >
          <span className="tab-icon">🤖</span>
          <span className="tab-label">Android</span>
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Version Info Component (M004-D004-F006)
// ============================================================================
const VersionInfo = ({ platform, versionData }) => {
  const info = platform === 'ios' ? versionData?.ios : versionData?.android;
  const validVersion = typeof info?.version === 'string' && info.version.trim().length > 0;
  const validCode = platform !== 'android' || (Number.isSafeInteger(info?.versionCode) && info.versionCode > 0);
  if (!validVersion || !validCode) {
    return <div className="version-info" role="status">Version information unavailable</div>;
  }

  return (
    <div className="version-info">
      <div className="version-item">
        <span className="version-label">Version:</span>
        <span className="version-value">{info.version}{platform === 'android' ? ` (build ${info.versionCode})` : ''}</span>
      </div>
      {typeof info.size === 'string' && info.size && (
        <div className="version-item">
          <span className="version-label">Size:</span>
          <span className="version-value">{info.size}</span>
        </div>
      )}
      {typeof info.lastUpdated === 'string' && info.lastUpdated && (
        <div className="version-item">
          <span className="version-label">Last Updated:</span>
          <span className="version-value">{info.lastUpdated}</span>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Download Button Component (M004-D004-F005)
// ============================================================================
const DownloadButton = ({ platform, onDownload, loading }) => {
  return (
    <button 
      className={`download-btn ${platform}`} 
      onClick={onDownload}
      disabled={loading}
    >
      {loading ? (
        <>
          <span className="spinner-small"></span>
          <span>Preparing download...</span>
        </>
      ) : (
        <>
          <span className="download-icon">⬇️</span>
          <span>Download {platform === 'android' ? 'APK' : 'App'}</span>
        </>
      )}
    </button>
  );
};

// ============================================================================
// Security Notice Component (M004-D004-F010)
// ============================================================================
const SecurityNotice = ({ sha256 }) => {
  return (
    <div className="security-notice">
      <div className="security-header">
        <span className="security-icon">🔒</span>
        <span>Security Information</span>
      </div>
      <p>
        This APK is signed with our official signing key. Only download from this
        page to ensure you have the authentic StudioAEye application.
      </p>
      {typeof sha256 === 'string' && sha256 && sha256 !== 'Unknown' && (
        <div className="sha256-fingerprint">
          <span className="sha-label">SHA-256 Fingerprint:</span>
          <code className="sha-value">{sha256.substring(0, 32)}...</code>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// iOS Section Component (M004-D004-F003, F004, F008)
// ============================================================================
const IOSSection = ({ isMobile }) => {
  return (
    <div className="platform-section ios-section">
      {/* Expo Server Status */}
      <ExpoServerStatus />

      {/* Step 1: Install Expo Go */}
      <div className="install-step">
        <div className="step-header">
          <span className="step-number">1</span>
          <h3>Install Expo Go</h3>
        </div>
        <p className="step-description">
          First, download the free <strong>Expo Go</strong> app from the Apple App Store.
          This app is required to run StudioAEye on iOS devices.
        </p>
        
        {!isMobile ? (
          <QRCodeDisplay
            type="expo-go"
            title="Expo Go App Store"
            description="Scan with your iPhone camera"
            fallbackUrl="https://apps.apple.com/app/expo-go/id982107779"
            fallbackLabel="Open App Store"
          />
        ) : (
          <a 
            href="https://apps.apple.com/app/expo-go/id982107779" 
            target="_blank" 
            rel="noopener noreferrer"
            className="mobile-download-link"
          >
            <span>📱</span> Open App Store to Download Expo Go
          </a>
        )}
        
        <div className="install-instructions">
          <h4>Instructions:</h4>
          <ol>
            <li>Open the Camera app on your iPhone or iPad</li>
            <li>Scan the QR code above to open the App Store</li>
            <li>Tap "Get" to download Expo Go</li>
            <li>Wait for installation to complete</li>
          </ol>
        </div>
      </div>

      {/* Step 2: Load StudioAEye App */}
      <div className="install-step">
        <div className="step-header">
          <span className="step-number">2</span>
          <h3>Open StudioAEye App</h3>
        </div>
        <p className="step-description">
          Once Expo Go is installed, scan this QR code to load the StudioAEye app.
        </p>
        
        {!isMobile ? (
          <QRCodeDisplay
            type="ios-app"
            title="StudioAEye App"
            description="Open Expo Go and scan this code"
            fallbackUrl="exp://studioaeye-app.exp.direct"
            fallbackLabel="Open in Expo Go"
          />
        ) : (
          <a 
            href="exp://studioaeye-app.exp.direct" 
            className="mobile-download-link expo-link"
          >
            <span>📲</span> Open StudioAEye in Expo Go
          </a>
        )}
        
        <div className="install-instructions">
          <h4>Instructions:</h4>
          <ol>
            <li>Open the Expo Go app on your device</li>
            <li>Tap "Scan QR Code"</li>
            <li>Scan the QR code above</li>
            <li>The StudioAEye app will download and launch</li>
          </ol>
        </div>
        
        <div className="info-notice">
          <span className="notice-icon">ℹ️</span>
          <span>
            <strong>Note:</strong> The app requires an active internet connection to the Expo server.
            Updates are instant - you'll always get the latest version when opening the app.
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Android Section Component (M004-D004-F005, F009, F010)
// ============================================================================
const ANDROID_APK_URL = 'https://studioaeye.com/downloads/studioaeye.apk';

const AndroidSection = ({ isMobile, versionData }) => {
  const handleDirectDownload = () => {
    window.location.href = ANDROID_APK_URL;
  };

  return (
    <div className="platform-section android-section">
      <div className="install-step">
        <div className="step-header">
          <span className="step-number">📱</span>
          <h3>Download StudioAEye for Android</h3>
        </div>
        <p className="step-description">
          Download the native Android app directly. No additional apps required.
        </p>
        
        {/* Version Info */}
        <VersionInfo platform="android" versionData={versionData} />
        
        {/* QR Code or Direct Download */}
        {!isMobile ? (
          <QRCodeDisplay
            type="android-apk"
            title="Android APK Download"
            description="Scan with your Android phone camera"
            fallbackUrl={ANDROID_APK_URL}
            fallbackLabel="Download APK"
          />
        ) : (
          <div className="mobile-download-section">
            <DownloadButton 
              platform="android" 
              onDownload={handleDirectDownload}
              loading={false}
            />
          </div>
        )}
        
        <div className="install-instructions">
          <h4>First installation:</h4>
          <ol>
            <li>
              <strong>Download the APK</strong>
              <p>Scan the QR code or tap the download button</p>
            </li>
            <li>
              <strong>Enable Unknown Sources</strong> (if prompted)
              <p>Go to Settings → Security → Enable "Install from Unknown Sources"</p>
            </li>
            <li>
              <strong>Install the App</strong>
              <p>Open the downloaded APK file and tap "Install"</p>
            </li>
            <li>
              <strong>Open the App</strong>
              <p>Find StudioAEye in your app drawer and log in with your credentials</p>
            </li>
          </ol>
        </div>
        
        <div className="install-instructions">
          <h4>Update an existing installation:</h4>
          <p>Keep the existing app installed to preserve app data, keys and two-step verification (2SV).
            Download the official APK above, open it and approve the in-place update in the Android installer.</p>
          <p>If installation fails, stop and keep the existing app. Record the exact installer error
            and contact support@studioaeye.com before taking further action.</p>
        </div>

        {/* Security Notice */}
        <SecurityNotice sha256={versionData?.android?.sha256} />
      </div>
    </div>
  );
};

// ============================================================================
// Troubleshooting Section (M004-D004-F015)
// ============================================================================
const TroubleshootingSection = ({ platform }) => {
  const iosIssues = [
    { issue: '"Unable to connect"', solution: 'The Expo server may be temporarily unavailable. Please try again in a few minutes.' },
    { issue: 'QR code won\'t scan', solution: 'Make sure Expo Go is open and tap "Scan QR Code" within the app.' },
    { issue: 'App loads slowly', solution: 'The first load downloads the app bundle. Subsequent loads will be faster.' },
    { issue: 'App doesn\'t update', solution: 'Close Expo Go completely and reopen the app to get the latest version.' },
  ];
  
  const androidIssues = [
    { issue: '"Install blocked"', solution: 'Enable "Install from Unknown Sources" in Settings → Security.' },
    { issue: 'Download fails', solution: 'Check your internet connection and try the direct download link.' },
    { issue: 'App won\'t install', solution: 'Stop and keep the existing app, data, keys and 2SV. Record the exact installer error and contact support@studioaeye.com.' },
    { issue: '"App not installed" error', solution: 'Keep the existing app installed. Record the exact installer error and contact support@studioaeye.com.' },
  ];
  
  const issues = platform === 'ios' ? iosIssues : androidIssues;

  return (
    <div className="troubleshooting-section">
      <h3>🔧 Troubleshooting</h3>
      <div className="issues-grid">
        {issues.map((item, index) => (
          <div key={index} className="issue-card">
            <div className="issue-problem">{item.issue}</div>
            <div className="issue-solution">{item.solution}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Main Mobile Download Component
// ============================================================================
const MobileDownload = ({ embedded = false }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('ios');
  const [versionData, setVersionData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device (M004-D004-F016)
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      setIsMobile(isMobileDevice);
      
      // Auto-select platform based on device
      if (isMobileDevice) {
        if (/android/i.test(userAgent)) {
          setActiveTab('android');
        } else if (/iphone|ipad|ipod/i.test(userAgent)) {
          setActiveTab('ios');
        }
      }
    };
    
    checkMobile();
  }, []);

  // Fetch version information
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await fetch('/api/mobile/version', {
          headers: buildAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          setVersionData(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch version info:', err);
      }
    };

    fetchVersion();
  }, []);

  const content = (
      <div className="mobile-download-page">
        {/* Page Header (M004-D004-F001) */}
        <header className="page-header">
          <h1>📱 Download Mobile App</h1>
          <p className="subtitle">
            Get the StudioAEye app on your mobile device for booking management and climate control on the go.
          </p>
        </header>

        {/* Platform Tabs */}
        <PlatformTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Platform-specific content */}
        <div className="platform-content">
          {activeTab === 'ios' ? (
            <IOSSection isMobile={isMobile} />
          ) : (
            <AndroidSection isMobile={isMobile} versionData={versionData} />
          )}
        </div>

        {/* Troubleshooting */}
        <TroubleshootingSection platform={activeTab} />

        {/* Footer info */}
        <div className="download-footer">
          <p>
            Need help? Contact support at <a href="mailto:support@studioaeye.com">support@studioaeye.com</a>
          </p>
        </div>
      </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout>
      {content}
    </Layout>
  );
};

export default MobileDownload;
